import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '@/config/api';

// BOH Roles that have access to admin portal
const BOH_ROLES = [
  'super_admin',
  'manager',
  'operations_manager',
  'kitchen',
  'fulfilment',
  'customer_support',
  'finance',
  'driver',
  'admin',
  'staff'
];

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Token stored in memory only — NOT in localStorage (security: XSS protection)
  // The httpOnly cookie keeps the session alive; this is used for Authorization headers
  const [token, setToken] = useState(null);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Use credentials: 'include' to send the httpOnly session cookie
      // The backend returns 200 always (with authenticated:true/false) to avoid 401 console noise
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.access_token) {
          setUser(data);
          setToken(data.access_token);
        } else {
          // Not authenticated
          setUser(null);
          setToken(null);
          if (data.blocked) setBlockedInfo(data.blocked_message || 'Your account has been permanently blocked for attempting to breach platform security.');
        }
      } else {
        setUser(null);
        setToken(null);
        if (response.status === 403) {
          const d = await response.json().catch(() => ({}));
          const det = String(d.detail || '');
          if (det.startsWith('ACCOUNT_BLOCKED')) setBlockedInfo(det.replace('ACCOUNT_BLOCKED: ', ''));
        }
      }
    } catch {
      // Network error — silently fail, keep existing state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (accessToken, userData) => {
    // Store token in memory only (httpOnly cookie handles persistence)
    setToken(accessToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
      }
    } catch {
      // Ignore logout errors
    } finally {
      setToken(null);
      setUser(null);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    blockedInfo,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.role && BOH_ROLES.includes(user.role),
    isBOH: user?.role && BOH_ROLES.includes(user.role)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
